// v2 snowy owl — the ghost of the snowfields in flight: a plump body under a big
// round head with a flat facial disc, large amber eyes with pupils and a tiny
// dark beak, broad rounded finger-less wings (owl-silent), a fanned tail and
// tucked feathered feet. Variant 0 is the dark-flecked female, variant 1 the
// nearly pure-white male — the flecking itself is the variant. Ported 1:1 from
// age-of-mcp's v2/snowy-owl.ts, using the library's shared `wildland` (wingPair)
// helper.
import * as THREE from "three";
import { defineModel, wildland, type Part } from "../../lib";

const FLECKS: Array<[number, number, number]> = [
  [-0.012, 0.075, 0.024], [-0.022, 0.06, -0.02], [0.0, 0.058, 0.03],
  [-0.03, 0.05, 0.008], [0.012, 0.07, -0.026], [-0.005, 0.0555, -0.046],
  [-0.005, 0.0555, 0.046],
];

function snowyOwlParts(v: number): Part[] {
  const fleckCount = v % 2 === 0 ? FLECKS.length : 1;
  const parts: Part[] = [
    // plump body
    { geometry: new THREE.IcosahedronGeometry(0.042, 0), color: "#f7f9fb",
      position: [0, 0.042, 0], scale: [1.35, 1.05, 0.95] },
    // big round head + flat facial disc
    { geometry: new THREE.IcosahedronGeometry(0.032, 0), color: "#ffffff",
      position: [0.028, 0.092, 0], scale: [1.05, 0.95, 1.05] },
    { geometry: new THREE.IcosahedronGeometry(0.024, 0), color: "#f1f4f6",
      position: [0.05, 0.09, 0], scale: [0.4, 1.0, 1.1] },
    // tiny dark beak
    { geometry: new THREE.ConeGeometry(0.005, 0.012, 4), color: "#2e2822",
      position: [0.062, 0.082, 0], rotation: [0, 0, -Math.PI / 2] },
    // fanned tail
    { geometry: new THREE.ConeGeometry(0.018, 0.05, 4), color: "#f4f7f9",
      position: [-0.06, 0.04, 0], rotation: [0, 0, Math.PI / 2],
      scale: [1, 1, 0.4] },
  ];
  for (const s of [1, -1] as const) {
    parts.push(
      // large amber eyes with pupils
      { geometry: new THREE.IcosahedronGeometry(0.0085, 0), color: "#f2a51c",
        position: [0.058, 0.096, s * 0.0145] },
      { geometry: new THREE.IcosahedronGeometry(0.0035, 0), color: "#1c1812",
        position: [0.064, 0.096, s * 0.0145] },
      // tucked feathered feet
      { geometry: new THREE.IcosahedronGeometry(0.007, 0), color: "#f1f4f6",
        position: [0.012, 0.018, s * 0.012], scale: [1.2, 0.7, 0.8] },
      // rounded wingtip pads (no fingers — owls fly silent)
      { geometry: new THREE.IcosahedronGeometry(0.0105, 0), color: "#eef2f5",
        position: [-0.004, 0.0665, s * 0.155], scale: [1.6, 0.5, 1.0] },
    );
  }
  parts.push(...wildland.wingPair({
    root: [0.0, 0.05, 0.022], span: 0.135, chord: 0.062, rise: 0.018,
    sweep: 0.01, thick: 0.0075,
    inner: "#f6f9fb", outer: "#eef2f5", fingers: 0,
  }));
  // dark barring flecks — the female wears them all, the male barely any
  for (let i = 0; i < fleckCount; i++) {
    const p = FLECKS[i]!;
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.005, 0),
      color: "#99a1a8", position: [p[0], p[1], p[2]],
      scale: [1.3, 0.5, 1.0] });
  }
  return parts;
}

export const snowyOwlV2 = defineModel({
  id: "snowy-owl-v2",
  batchId: "fable",
  name: "Snowy owl (v2)",
  tags: ["creature","snowy","owl","low-poly","age-of-mcp","fable"],
  summary: "Low-poly snowy owl.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.09,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => snowyOwlParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => snowyOwlParts(1) },
  ],
  animations: [],
});
